import { useEffect, useRef } from 'react';
import { App } from '@capacitor/app';
import { GoogleCalendarService } from '@/services/GoogleCalendarService';
import { useUIStore } from '@/stores/uiStore';
import { useHydration } from '@/hooks/useHydration';
import { isNative } from '@/lib/platform';
import { scheduleNotifications } from '@/features/notifications/scheduleNotifications';

export function useAppLifecycle(): void {
  const hydrated = useHydration();
  const didColdLaunch = useRef(false);

  // Native lifecycle: register the resume listener + the initial notification
  // schedule once on mount. These don't read persisted domain state, so they
  // don't need to wait for hydration.
  useEffect(() => {
    if (!isNative()) return;

    const handle = App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return;
      if (useUIStore.getState().googleAccessToken) {
        void GoogleCalendarService.sync().catch(() => undefined);
      }
      void scheduleNotifications();
    });

    // Initial schedule on mount
    void scheduleNotifications();

    return () => {
      void handle.then((h) => h.remove());
    };
  }, []);

  // Cold-launch work that READS persisted state must wait for hydration. On
  // native, zustand-persist (Capacitor Preferences) hydrates ASYNCHRONOUSLY, so
  // reading getState() synchronously at mount returns pre-hydration defaults — a
  // stale lastViewedDate would never be corrected and the token gate would skip
  // the calendar sync entirely. Run exactly once, after hydration lands.
  useEffect(() => {
    if (!hydrated || didColdLaunch.current) return;
    didColdLaunch.current = true;

    // Open on TODAY. lastViewedDate is persisted; a stale value anchors the Today
    // header to the wrong day → "Open day" even when today is full.
    const today = new Date();
    const viewed = useUIStore.getState().lastViewedDate;
    const sameDay =
      viewed.getFullYear() === today.getFullYear() &&
      viewed.getMonth() === today.getMonth() &&
      viewed.getDate() === today.getDate();
    if (!sameDay) useUIStore.getState().setLastViewedDate(today);

    // Cold-launch calendar sync: appStateChange only fires on resume, so a fresh
    // native process never refreshes events otherwise → the day looked empty.
    if (isNative() && useUIStore.getState().googleAccessToken) {
      void GoogleCalendarService.sync().catch(() => undefined);
    }
  }, [hydrated]);
}
